import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Crosshair, RefreshCw, AlertTriangle } from 'lucide-react';
import { getApiUrl, getAuthenticatedAxios } from '../../utils/api';

const API_URL = getApiUrl();

// Auto-snipe: when BLUM discovers a token whose name/symbol EXACTLY matches the
// target, all wallets of the chosen group buy it (fixed TON or full balance).
const BlumAutoSnipe = () => {
  const [cfg, setCfg] = useState(null);
  const [groups, setGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // positions / mass sell
  const [posToken, setPosToken] = useState('');
  const [holders, setHolders] = useState(null);
  const [selected, setSelected] = useState({});
  const [loadingHolders, setLoadingHolders] = useState(false);
  const [selling, setSelling] = useState(false);
  const [sellPercent, setSellPercent] = useState(100);
  const [deploying, setDeploying] = useState(false);
  const [positions, setPositions] = useState(null);
  const [loadingPositions, setLoadingPositions] = useState(false);

  // NOTE: create the axios instance inside callbacks and keep load() stable
  // ([] deps) so the effect runs ONCE on mount. Otherwise a fresh axios per
  // render would re-create load -> re-run the effect -> reset cfg on every
  // keystroke (input/select would never hold a value).
  const load = useCallback(async () => {
    const api = getAuthenticatedAxios();
    try {
      const [c, g, h] = await Promise.all([
        api.get(`${API_URL}/api/blum/auto-snipe/config`),
        api.get(`${API_URL}/api/wallets/groups`).catch(() => ({ data: { groups: [] } })),
        api.get(`${API_URL}/api/blum/auto-snipe/history?limit=10`).catch(() => ({ data: { history: [] } })),
      ]);
      setCfg(c.data);
      setGroups(g.data.groups || []);
      setHistory(h.data.history || []);
    } catch (e) {
      toast.error('Не удалось загрузить настройки auto-snipe');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (cfg.enabled && !String(cfg.target_name || '').trim()) {
      toast.error('Укажите название токена (target_name) перед включением');
      return;
    }
    setSaving(true);
    try {
      const api = getAuthenticatedAxios();
      const res = await api.post(`${API_URL}/api/blum/auto-snipe/config`, cfg);
      setCfg(res.data.config);
      toast.success('Настройки auto-snipe сохранены');
    } catch (e) {
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const loadPositions = async () => {
    setLoadingPositions(true);
    try {
      const api = getAuthenticatedAxios();
      const res = await api.get(`${API_URL}/api/blum/auto-snipe/positions`, {
        params: { group: cfg.wallet_group || '', refresh: 1 },
      });
      setPositions(res.data);
    } catch (e) {
      toast.error('Не удалось определить позиции');
    } finally {
      setLoadingPositions(false);
    }
  };

  const loadHolders = async (tok) => {
    const t = String(tok ?? posToken).trim();
    if (!t) { toast.error('Выберите токен из списка позиций'); return; }
    setLoadingHolders(true);
    try {
      const api = getAuthenticatedAxios();
      const res = await api.get(`${API_URL}/api/blum/auto-snipe/holders`, {
        params: { token_address: t, group: cfg.wallet_group || '', refresh: 1 },
      });
      setHolders(res.data);
      setSelected({});
    } catch (e) {
      toast.error('Не удалось получить список холдеров');
    } finally {
      setLoadingHolders(false);
    }
  };

  const selectPosition = (p) => {
    setPosToken(p.token_address);
    setHolders(null);
    loadHolders(p.token_address);
  };

  const toggleAll = (checked) => {
    const m = {};
    if (checked && holders) holders.holders.forEach((h) => { m[h.wallet] = true; });
    setSelected(m);
  };

  const doSell = async (sellAll) => {
    if (!holders || holders.count === 0) { toast.error('Нет холдеров'); return; }
    const wallets = sellAll ? null : Object.keys(selected).filter((w) => selected[w]);
    if (!sellAll && (!wallets || wallets.length === 0)) { toast.error('Выберите кошельки'); return; }
    const n = sellAll ? holders.count : wallets.length;
    if (!window.confirm(`Продать ${sellPercent}% токена с ${n} кошельков? Это реальная продажа.`)) return;
    setSelling(true);
    try {
      const api = getAuthenticatedAxios();
      const res = await api.post(`${API_URL}/api/blum/auto-snipe/sell`, {
        token_address: posToken.trim(), wallets, group: cfg.wallet_group || null, percent: Number(sellPercent),
      });
      const d = res.data;
      if (d.success) toast.success(`Продано ${d.sold}/${d.wallets_total} кошельков за ${d.elapsed_sec}s`);
      else toast.error(`Продажа: ${d.reason || 'ошибка'}`);
      loadHolders();
    } catch (e) {
      toast.error('Ошибка продажи');
    } finally {
      setSelling(false);
    }
  };

  const deployWallets = async () => {
    if (!window.confirm(`Инициализировать (задеплоить) кошельки группы "${cfg.wallet_group || 'все'}"? Спишется немного газа (~0.005 TON с каждого недеплоенного кошелька). Нужно один раз перед торговлей.`)) return;
    setDeploying(true);
    try {
      const api = getAuthenticatedAxios();
      const res = await api.post(`${API_URL}/api/blum/auto-snipe/deploy-wallets`, { group: cfg.wallet_group || null });
      const d = res.data;
      toast.success(`Инициализировано ${d.deployed}/${d.total} (уже активных/ошибок: ${d.failed})`);
    } catch (e) {
      toast.error('Ошибка инициализации кошельков');
    } finally {
      setDeploying(false);
    }
  };

  const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');
  const selectedCount = Object.values(selected).filter(Boolean).length;

  if (loading || !cfg) {
    return <div className="flex items-center gap-2 text-white/70 p-6"><RefreshCw className="h-4 w-4 animate-spin" /> Загрузка…</div>;
  }

  return (
    <div className="space-y-4">
      <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Crosshair className="h-5 w-5" /> Auto-Snipe по названию токена</CardTitle>
          <CardDescription className="text-white/70">
            Как только BLUM обнаруживает токен с точным совпадением названия/символа — все кошельки выбранной группы покупают его.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cfg.enabled && (
            <div className="flex items-start gap-2 rounded-md bg-red-500/20 border border-red-400/40 p-3 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-red-300" />
              <span>Боевой режим включён: при первом же совпадении будут реальные покупки на реальные средства.</span>
            </div>
          )}

          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} className="h-5 w-5 rounded" />
            <span className="font-semibold">Включить auto-snipe</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/80">Название токена (точное совпадение, без учёта регистра)</label>
              <Input value={cfg.target_name || ''} onChange={(e) => set('target_name', e.target.value)} placeholder="напр. PUBG" className="bg-white/10 border-white/20" />
            </div>
            <div>
              <label className="text-sm text-white/80">Группа кошельков</label>
              <select value={cfg.wallet_group || ''} onChange={(e) => set('wallet_group', e.target.value)} className="w-full text-sm rounded p-2 bg-slate-800 border border-white/20">
                <option value="">— Все кошельки —</option>
                {groups.map((g) => (
                  <option key={g.group_name} value={g.group_name}>
                    {g.group_name || 'No Group'}{typeof g.count === 'number' ? ` (${g.count})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-white/80">Сумма покупки</label>
              <select value={cfg.amount_mode || 'fixed'} onChange={(e) => set('amount_mode', e.target.value)} className="w-full text-sm rounded p-2 bg-slate-800 border border-white/20">
                <option value="fixed">Фиксированная сумма TON на кошелёк</option>
                <option value="full_balance">Полный баланс кошелька (минус резерв)</option>
              </select>
            </div>
            {cfg.amount_mode === 'full_balance' ? (
              <div>
                <label className="text-sm text-white/80">Резерв на газ (TON на кошелёк)</label>
                <Input type="number" step="0.01" value={cfg.balance_reserve_ton} onChange={(e) => set('balance_reserve_ton', e.target.value)} className="bg-white/10 border-white/20" />
              </div>
            ) : (
              <div>
                <label className="text-sm text-white/80">TON на кошелёк</label>
                <Input type="number" step="0.1" value={cfg.ton_per_wallet} onChange={(e) => set('ton_per_wallet', e.target.value)} className="bg-white/10 border-white/20" />
              </div>
            )}
            <div>
              <label className="text-sm text-white/80">Слиппедж, % (для продажи)</label>
              <Input type="number" step="0.5" value={cfg.slippage_percent} onChange={(e) => set('slippage_percent', e.target.value)} className="bg-white/10 border-white/20" />
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <input type="checkbox" checked={cfg.accept_any_amount !== false} onChange={(e) => set('accept_any_amount', e.target.checked)} className="h-4 w-4 rounded" />
              <span className="text-sm text-white/80">Покупать без защиты от слиппеджа (гарантированный заход — рекомендуется для снайпа)</span>
            </div>
            <div>
              <label className="text-sm text-white/80">Макс. одновременных покупок (0 = все сразу)</label>
              <Input type="number" step="1" value={cfg.max_concurrent} onChange={(e) => set('max_concurrent', e.target.value)} className="bg-white/10 border-white/20" />
            </div>
            <div>
              <label className="text-sm text-white/80">Лимит общей суммы за снайп, TON (пусто = без лимита)</label>
              <Input type="number" step="1" value={cfg.max_total_ton ?? ''} onChange={(e) => set('max_total_ton', e.target.value === '' ? null : e.target.value)} className="bg-white/10 border-white/20" />
            </div>
            <div>
              <label className="text-sm text-white/80">Мин. покупка на кошелёк, TON</label>
              <Input type="number" step="0.01" value={cfg.min_buy_ton} onChange={(e) => set('min_buy_ton', e.target.value)} className="bg-white/10 border-white/20" />
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="w-full">
            {saving ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Сохранение…</> : 'Сохранить настройки'}
          </Button>

          <div className="rounded-md bg-amber-500/15 border border-amber-400/30 p-3 space-y-2">
            <p className="text-xs text-amber-200">
              ⚠️ Новые кошельки нужно один раз <b>инициализировать</b> (задеплоить), иначе покупки не пройдут
              (у недеплоенного кошелька нет seqno). Делается один раз для группы.
            </p>
            <Button onClick={deployWallets} disabled={deploying} variant="outline" className="w-full">
              {deploying ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Инициализация…</> : 'Инициализировать кошельки группы'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
        <CardHeader>
          <CardTitle className="text-base">Позиции по токену — продажа</CardTitle>
          <CardDescription className="text-white/70">
            Реальное время: какие кошельки держат токен и сколько. Выбери кошельки и продай все токены.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={loadPositions} disabled={loadingPositions} variant="outline" className="w-full">
            {loadingPositions ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Определяю позиции…</> : '🔄 Обновить позиции (что куплено кошельками)'}
          </Button>

          {positions && positions.count > 0 && (
            <div className="space-y-1">
              <div className="text-xs text-white/60">Найдено токенов: {positions.count}. Выбери токен:</div>
              {positions.positions.map((p) => (
                <button
                  key={p.token_address}
                  onClick={() => selectPosition(p)}
                  className={`w-full text-left flex justify-between items-center p-2 rounded border transition ${posToken === p.token_address ? 'border-blue-400 bg-blue-500/20' : 'border-white/10 hover:bg-white/5'}`}
                >
                  <span className="font-semibold">{p.symbol || 'UNKNOWN'}{p.name && p.name !== p.symbol ? ` · ${p.name}` : ''}</span>
                  <span className="text-white/70 text-sm">{Number(p.total_amount).toLocaleString()} · {p.wallets} кош.</span>
                </button>
              ))}
            </div>
          )}
          {positions && positions.count === 0 && (
            <div className="text-sm text-white/60">Кошельки группы сейчас не держат токенов.</div>
          )}

          <details className="text-xs text-white/50">
            <summary className="cursor-pointer">или указать адрес вручную</summary>
            <div className="flex gap-2 mt-2">
              <Input value={posToken} onChange={(e) => setPosToken(e.target.value)} placeholder="0:... или EQ..." className="bg-white/10 border-white/20 flex-1" />
              <Button onClick={() => loadHolders()} disabled={loadingHolders} variant="outline">Загрузить</Button>
            </div>
          </details>

          {holders && (
            <>
              <div className="text-sm text-white/80">
                Холдеров: <b>{holders.count}</b> · всего токенов: <b>{Number(holders.total_amount).toLocaleString()}</b>
                {holders.count > 0 && <> · выбрано: <b>{selectedCount}</b></>}
              </div>

              {holders.count > 0 ? (
                <div className="max-h-72 overflow-auto rounded border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-800">
                      <tr>
                        <th className="p-2 text-left">
                          <input
                            type="checkbox"
                            checked={selectedCount === holders.count && holders.count > 0}
                            onChange={(e) => toggleAll(e.target.checked)}
                          />
                        </th>
                        <th className="p-2 text-left">Кошелёк</th>
                        <th className="p-2 text-right">Токенов</th>
                        <th className="p-2 text-right">TON</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holders.holders.map((h) => (
                        <tr key={h.wallet} className="border-t border-white/10">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={!!selected[h.wallet]}
                              onChange={(e) => setSelected((p) => ({ ...p, [h.wallet]: e.target.checked }))}
                            />
                          </td>
                          <td className="p-2 font-mono">{shortAddr(h.wallet)}</td>
                          <td className="p-2 text-right">{Number(h.amount).toLocaleString()}</td>
                          <td className="p-2 text-right text-white/60">{h.ton_balance}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-white/60">Нет кошельков с этим токеном.</div>
              )}

              {holders.count > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-white/80">Продать</span>
                  <Input
                    type="number" step="1" min="1" max="100"
                    value={sellPercent}
                    onChange={(e) => setSellPercent(e.target.value)}
                    className="bg-white/10 border-white/20 w-20"
                  />
                  <span className="text-sm text-white/80">% баланса</span>
                  <Button onClick={() => doSell(false)} disabled={selling || selectedCount === 0} variant="outline">
                    {selling ? <RefreshCw className="h-4 w-4 animate-spin" /> : `Продать выбранные (${selectedCount})`}
                  </Button>
                  <Button onClick={() => doSell(true)} disabled={selling} className="bg-red-600 hover:bg-red-700">
                    Продать со ВСЕХ ({holders.count})
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
          <CardHeader><CardTitle className="text-base">Последние срабатывания</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {history.map((h) => (
              <div key={h._id} className="flex justify-between border-b border-white/10 pb-1">
                <span>{h.token_symbol || h.token_name} · {h.wallet_group}</span>
                <span className="text-white/70">{h.bought}/{h.wallets_total} куплено · {Number(h.total_ton).toFixed(2)} TON</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BlumAutoSnipe;
